import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useGallery,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';
import { HeroActionRail } from './HeroActionRail';
import { HeroSubtitleRichText } from './HeroSubtitleRichText';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  subtitle?: string;
  subtitleDefault?: string;
  blurbField?: string;
  defaultText?: string;
  /** Suppress the inline blurb below the hero (e.g. when a page renders it as its own PageBlurb section). */
  hideBlurb?: boolean;
}

const FIELD = (name: string) =>
  `<span data-type="field-token" data-field="${name}"></span>`;

function buildSubtitleDefaults(): Record<string, string> {
  return {
    'interactive-map-hero':
      `<p>Availability at ${FIELD('property_name')} in ${FIELD('property_address_city')}</p>`,
    'floor-plans-hero':
      `<p>${FIELD('property_bedrooms_range')} bedroom apartments for rent in ${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
    'virtual-tour-hero':
      `<p>Tour our ${FIELD('property_bedrooms_range')} apartments at ${FIELD('property_name')}</p>`,
    'amenities-hero':
      `<p>Comfort and convenience at ${FIELD('property_name')}</p>`,
    'pet-policy-hero':
      `<p>Pet-friendly living at ${FIELD('property_name')}</p>`,
    'gallery-hero':
      `<p>See life at ${FIELD('property_name')}</p>`,
    'neighborhood-hero':
      `<p>${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
    'contact-hero':
      `<p>Reach our leasing team today</p>`,
    'resident-access-hero':
      `<p>Pay rent and manage your account</p>`,
    'schedule-tour-hero':
      `<p>Visit ${FIELD('property_name')} in ${FIELD('property_address_city')}</p>`,
    'reviews-hero':
      `<p>Resident stories from ${FIELD('property_name')}</p>`,
    'faq-hero':
      `<p>Answers to the questions residents ask most at ${FIELD('property_name')}</p>`,
    'privacy-policy-hero':
      `<p>How we protect your information</p>`,
    'sitemap-hero':
      `<p>Find every page quickly</p>`,
    'accessibility-hero':
      `<p>Inclusive access for every visitor</p>`,
    'apply-now-hero':
      `<p>Apply for ${FIELD('property_bedrooms_range')} apartments at ${FIELD('property_name')}</p>`,
    'preferred-employer-hero':
      `<p>Exclusive move-in savings at ${FIELD('property_name')}</p>`,
    'sister-properties-hero':
      `<p>Explore nearby apartment communities from ${FIELD('property_company_name')} near ${FIELD('property_name')}</p>`,
    'thank-you-hero':
      `<p>We'll be in touch soon</p>`,
  };
}

const SUBTITLE_DEFAULTS = buildSubtitleDefaults();

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasBlurbContent(value: string | null | undefined): boolean {
  return Boolean(stripHtml(value || '').trim());
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  subtitleDefault,
  blurbField,
  defaultText,
  hideBlurb = false,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;
  const bgPath = `${prefix}.bg_image`;

  const blurbContent = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const showBlurb = !hideBlurb && hasBlurbContent(blurbContent);
  const bgImage = useSlotGroup(bgPath, bgImageDef);
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const pageHint = prefix;
  const keywords = useMemo(
    () => title.toLowerCase().split(/\s+/).filter(Boolean),
    [title],
  );

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  const resolvedSubtitleDefault =
    subtitleDefault ||
    subtitle ||
    SUBTITLE_DEFAULTS[prefix] ||
    `<p>Discover ${FIELD('property_name')}</p>`;

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="hero-solid-bg">
      {/* Photographic hero — title centered over featured image */}
      <div className="hero-solid-hero hero-rail-anchor relative w-full overflow-hidden">
        <EditableSlotGroup groupPath={bgPath} definition={bgImageDef} className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={bgImage.alt || title}
              priority
              sizes="100vw"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[var(--vibe-color-text)]" />
          )}
        </EditableSlotGroup>

        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/10" />

        <div className="hero-solid-title-wrap relative z-10 flex h-full flex-col items-center justify-center px-6 sm:px-8 text-center text-white">
          <HeroSubtitleRichText
            path={subtitlePath}
            defaultValue={resolvedSubtitleDefault}
            className="hero-solid-subtitle"
          />
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h2"
            className="hero-solid-title"
          />
        </div>

        <HeroActionRail variant="menu-only" />
      </div>

      {showBlurb ? (
        <div className="hero-solid-blurb bg-white text-[var(--vibe-color-text)]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 text-center">
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="hero-solid-blurb-text font-body text-base md:text-lg leading-relaxed [&_a]:underline [&_a]:text-[var(--vibe-color-primary)]"
            />
          </div>
        </div>
      ) : null}

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-solid-hero {
          /* Rail tile size matches the home hero (1/5 viewport), not this shorter hero. */
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 50vh;
          height: 50svh;
          height: 50dvh;
          max-height: 50vh;
          max-height: 50svh;
          max-height: 50dvh;
          min-height: 280px;
        }
        @supports (height: 100dvh) {
          .hero-solid-hero {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }
        /* The header is fixed and transparent over this hero. On short mobile
           viewports the vertically-centered title can slide behind it, so pad
           the centering region down past the header (plus any offer banner). */
        @media (max-width: 767px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 5.5rem);
          }
        }
        .hero-solid-title {
          font-family: "Raleway", sans-serif;
          font-size: 28px;
          font-weight: 700;
          line-height: 1.1;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 24ch;
        }
        .hero-solid-title p,
        .hero-solid-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        .hero-solid-subtitle {
          font-family: "Raleway", sans-serif;
          font-size: 17px;
          font-weight: 400;
          line-height: 1.4;
          letter-spacing: 0.02em;
          text-transform: none;
          color: rgba(255, 255, 255, 0.92);
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.4);
          margin: 0 0 0.875rem;
          max-width: min(40ch, 92vw);
          text-wrap: balance;
        }
        .hero-solid-subtitle p,
        .hero-solid-subtitle span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-title { font-size: 40px; }
          .hero-solid-subtitle {
            font-size: 20px;
            margin-bottom: 1.125rem;
            max-width: min(42ch, 90vw);
          }
        }
        @media (min-width: 1024px) {
          .hero-solid-title { font-size: 48px; }
          .hero-solid-subtitle {
            font-size: 22px;
            margin-bottom: 1.25rem;
          }
        }
        .hero-solid-blurb-text p {
          margin: 0;
        }
        .hero-solid-blurb-text p + p {
          margin-top: 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Discover <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
